import { DEFAULT_MODIFIERS } from '../combat/modifiers';
import {
  applyDiceStage,
  applyRollStage,
  applyRulesStage,
  finalOutcome,
  rollAttack,
  rollDefense,
  sortDesc,
  type CombatContext,
  type CombatOutcome,
} from '../combat/pipeline';
import { attackRoute, captainEffect, territoryCount } from '../selectors';
import { FALLBACK_MISSION_ID, type CombatSnapshot, type MissionDef, type PlayerId, type TerritoryId } from '../types';
import type { Ctx } from './context';
import { resolveBossCombat } from './boss';
import { drawTerritoryCard } from './cards';
import { gainHarmony } from './actions';
import { clearOwnerEffects, clearTerritoryEffects, removeEffect } from './effects';

function territoryContext(ctx: Ctx, p: PlayerId, from: TerritoryId, to: TerritoryId): CombatContext {
  const { s } = ctx;
  const route = attackRoute(s, p, from, to)!;
  const rs = s.info.ruleset;
  return {
    state: s,
    kind: 'territory',
    attacker: p,
    defender: s.territories[to].owner,
    from,
    to,
    route: route.route,
    sea: route.sea,
    attackDice: Math.min(rs.maxAttackDice, s.territories[from].troops - 1),
    defenseDice: Math.min(rs.maxDefenseDice, s.territories[to].troops),
    attackRolls: [],
    defenseRolls: [],
    tieGoesTo: 'defender',
    defenderWinsAll: false,
    notes: route.route === 'range' ? ['range'] : route.route === 'captain' ? ['captain'] : [],
  };
}

export function snapshotOf(c: CombatContext, untilEnd: boolean): CombatSnapshot {
  return {
    target: c.kind,
    from: c.from,
    to: c.to,
    sea: c.sea,
    attackRolls: c.attackRolls,
    defenseRolls: c.defenseRolls,
    tieGoesTo: c.tieGoesTo,
    defenderWinsAll: c.defenderWinsAll,
    notes: c.notes,
    reacted: [],
    untilEnd,
  };
}

/** Reconstrói o contexto de combate a partir de uma rolagem guardada. */
export function contextOf(ctx: Ctx, attacker: PlayerId, snap: CombatSnapshot): CombatContext {
  const { s } = ctx;
  return {
    state: s,
    kind: snap.target,
    attacker,
    defender: snap.to ? s.territories[snap.to].owner : null,
    from: snap.from,
    to: snap.to,
    route: snap.target === 'boss' ? 'lair' : 'land',
    sea: snap.sea,
    attackDice: snap.attackRolls.length,
    defenseDice: snap.defenseRolls.length,
    attackRolls: snap.attackRolls,
    defenseRolls: snap.defenseRolls,
    tieGoesTo: snap.tieGoesTo,
    defenderWinsAll: snap.defenderWinsAll,
    notes: snap.notes,
  };
}

/** Perdas da rolagem guardada, depois dos modificadores de resultado (Stone Skin). */
export function snapshotOutcome(ctx: Ctx, attacker: PlayerId, snap: CombatSnapshot): CombatOutcome {
  const c = contextOf(ctx, attacker, snap);
  const out = finalOutcome(c, DEFAULT_MODIFIERS);
  snap.notes = c.notes;
  return out;
}

/** Um ataque. Com `untilEnd`, repete até conquistar, sobrar 1 tropa ou abrir uma janela de reação. */
export function attack(ctx: Ctx, p: PlayerId, from: TerritoryId, to: TerritoryId, untilEnd: boolean): void {
  const { s } = ctx;
  const defender = s.territories[to].owner;
  do {
    const c = territoryContext(ctx, p, from, to);
    // Passagem do Capitão: o primeiro ataque trava a rota; só esse ataque vale no turno.
    if (c.route === 'captain') {
      const cap = captainEffect(s, p);
      if (cap && !cap.link) cap.link = [from, to];
    }
    applyDiceStage(c, DEFAULT_MODIFIERS);
    applyRulesStage(c, DEFAULT_MODIFIERS);
    rollAttack(c, ctx.rng);
    rollDefense(c, ctx.rng);
    applyRollStage(c, ctx.rng, 'initial', DEFAULT_MODIFIERS);
    afterRoll(ctx, p, snapshotOf(c, untilEnd));
  } while (
    untilEnd &&
    !s.pending &&
    s.phase !== 'over' &&
    s.territories[to].owner === defender &&
    s.territories[from].troops >= 2 &&
    attackRoute(s, p, from, to) !== null
  );
}

/** Quem tem o Amuleto da Sorte e ainda não decidiu nesta rolagem. */
function nextReactor(ctx: Ctx, attacker: PlayerId, snap: CombatSnapshot): { player: PlayerId; role: 'attacker' | 'defender' } | null {
  const { s } = ctx;
  const has = (p: PlayerId) => s.players[p]?.alive && s.players[p].loot.includes('luck') && !snap.reacted.includes(p);
  if (has(attacker)) return { player: attacker, role: 'attacker' };
  const defender = snap.to ? s.territories[snap.to].owner : null;
  if (defender !== null && defender !== attacker && has(defender)) return { player: defender, role: 'defender' };
  return null;
}

/** Depois de rolar: abre a janela de reação se alguém tem o amuleto; senão aplica as perdas. */
export function afterRoll(ctx: Ctx, p: PlayerId, snap: CombatSnapshot): void {
  const { s } = ctx;
  const reactor = nextReactor(ctx, p, snap);
  if (reactor) {
    s.pending = {
      kind: 'reaction',
      player: reactor.player,
      role: reactor.role,
      combat: snap,
      timeoutMs: s.info.ruleset.reactionTimeoutMs,
    };
    ctx.events.push({
      type: 'combatPreview',
      attacker: p,
      from: snap.from,
      to: snap.to,
      attackRolls: snap.attackRolls,
      defenseRolls: snap.defenseRolls,
      reactor: reactor.player,
    });
    return;
  }
  if (snap.target === 'boss') resolveBossCombat(ctx, p, snap);
  else resolveTerritoryCombat(ctx, p, snap);
}

/** Resposta à janela de reação. Usar o amuleto rerrola todos os dados de quem o usou. */
export function react(ctx: Ctx, player: PlayerId, use: boolean): void {
  const { s } = ctx;
  const pending = s.pending;
  if (pending?.kind !== 'reaction') return;
  s.pending = null;
  const attacker = s.current;
  const snap: CombatSnapshot = { ...pending.combat, notes: [...pending.combat.notes], reacted: [...pending.combat.reacted, player] };
  if (use) {
    const hand = s.players[player].loot;
    hand.splice(hand.indexOf('luck'), 1);
    s.lootDiscard.push('luck');
    ctx.events.push({ type: 'lootPlayed', player, kind: 'luck' });
    const c = contextOf(ctx, attacker, snap);
    if (pending.role === 'attacker') {
      c.attackRolls = sortDesc(ctx.rng.dice(snap.attackRolls.length));
      c.notes = c.notes.filter((n) => n !== 'strength' && n !== 'coordination' && n !== 'flow');
      applyRollStage(c, ctx.rng, 'reroll', DEFAULT_MODIFIERS);
      c.notes.push('luck');
    } else {
      c.defenseRolls = sortDesc(ctx.rng.dice(snap.defenseRolls.length));
      c.notes.push('luckDefender');
    }
    snap.attackRolls = c.attackRolls;
    snap.defenseRolls = c.defenseRolls;
    snap.notes = c.notes;
  }
  ctx.events.push({ type: 'reaction', player, used: use });
  afterRoll(ctx, attacker, snap);
  // "Atacar até o fim" pausou na janela: continua depois de resolver.
  if (snap.untilEnd && snap.target === 'territory' && !s.pending && s.phase !== 'over') {
    const to = snap.to!;
    if (s.territories[to].owner !== attacker && s.territories[snap.from].troops >= 2 && attackRoute(s, attacker, snap.from, to)) {
      attack(ctx, attacker, snap.from, to, true);
    }
  }
}

export function resolveTerritoryCombat(ctx: Ctx, p: PlayerId, snap: CombatSnapshot): void {
  const { s } = ctx;
  const to = snap.to!;
  const defender = s.territories[to].owner;
  const out = snapshotOutcome(ctx, p, snap);
  s.territories[snap.from].troops -= out.attackerLoss;
  s.territories[to].troops -= out.defenderLoss;
  s.turn.lost += out.attackerLoss;
  const hit = (s.attacked[defender] ??= []);
  if (!hit.includes(to)) hit.push(to);
  gainHarmony(ctx, p);
  const origins = (s.turn.attackedFrom[to] ??= []);
  if (!origins.includes(snap.from)) origins.push(snap.from);
  ctx.events.push({
    type: 'combat',
    attacker: p,
    defender,
    from: snap.from,
    to,
    attackRolls: snap.attackRolls,
    defenseRolls: snap.defenseRolls,
    attackerLoss: out.attackerLoss,
    defenderLoss: out.defenderLoss,
    sea: snap.sea,
    notes: snap.notes,
  });
  if (s.territories[to].troops <= 0) conquer(ctx, p, snap.from, to, snap.attackRolls.length);
}

function conquer(ctx: Ctx, p: PlayerId, from: TerritoryId, to: TerritoryId, diceUsed: number): void {
  const { s } = ctx;
  const defender = s.territories[to].owner;
  s.territories[to] = { owner: p, troops: 0 };
  clearTerritoryEffects(ctx, to);
  const cap = captainEffect(s, p);
  if (cap?.link && cap.link[0] === from && cap.link[1] === to) removeEffect(ctx, cap.id);
  s.turn.conquered++;
  ctx.events.push({ type: 'conquest', player: p, from, to, defender });
  eraOnConquest(ctx, p, defender);
  if (territoryCount(s, defender) === 0) eliminate(ctx, defender, p);
  const max = s.territories[from].troops - 1;
  const min = 1;
  if (max <= min) {
    moveIn(ctx, p, from, to, min);
    return;
  }
  const suggested = Math.max(min, Math.min(max, diceUsed));
  s.pending = { kind: 'conquest', player: p, from, to, min, max, suggested };
}

/** Regras de era ligadas à conquista: cabeça a prêmio e caveira. */
function eraOnConquest(ctx: Ctx, p: PlayerId, defender: PlayerId): void {
  const { s, rng } = ctx;
  const rs = s.info.ruleset;
  if (rs.bounty && s.bounty === defender && !s.turn.bountyClaimed) {
    s.turn.bountyClaimed = true;
    ctx.events.push({ type: 'bountyClaimed', player: p, target: defender });
    drawTerritoryCard(ctx, p);
  }
  if (rs.skullConquests > 0) {
    if (s.skulls.includes(defender)) {
      // Caveira vermelha: perde uma carta e um loot ao acaso, e a marca cai.
      s.skulls = s.skulls.filter((x) => x !== defender);
      const v = s.players[defender];
      let card = false;
      let loot = false;
      if (v.cards.length) {
        s.discard.push(v.cards.splice(rng.int(v.cards.length), 1)[0]);
        card = true;
      }
      if (v.loot.length) {
        s.lootDiscard.push(v.loot.splice(rng.int(v.loot.length), 1)[0]);
        loot = true;
      }
      ctx.events.push({ type: 'skullPenalty', player: defender, by: p, card, loot });
    }
    if (s.turn.conquered === rs.skullConquests && !s.skulls.includes(p)) {
      s.skulls.push(p);
      ctx.events.push({ type: 'skull', player: p });
    }
  }
}

export function moveIn(ctx: Ctx, p: PlayerId, from: TerritoryId, to: TerritoryId, count: number): void {
  const { s } = ctx;
  s.territories[from].troops -= count;
  s.territories[to].troops += count;
  s.pending = null;
  ctx.events.push({ type: 'moveIn', player: p, from, to, count });
}

function eliminate(ctx: Ctx, victim: PlayerId, by: PlayerId): void {
  const { s } = ctx;
  const v = s.players[victim];
  v.alive = false;
  v.eliminatedBy = by;
  s.eliminationOrder.push(victim);
  const cards = v.cards.length;
  s.players[by].cards.push(...v.cards);
  v.cards = [];
  s.lootDiscard.push(...v.loot);
  v.loot = [];
  clearOwnerEffects(ctx, victim);
  s.attacked[victim] = [];
  ctx.events.push({ type: 'eliminated', player: victim, by, cards });
  // Quem tinha a missão de eliminar essa guilda, mas não deu o golpe, recebe o objetivo reserva.
  for (const pl of s.players) {
    if (!pl.alive || pl.id === by) continue;
    if (pl.mission.type === 'eliminate' && pl.mission.color === v.color) {
      pl.mission = fallbackMission(pl.mission, s.info.ruleset.fallbackMissionTerritories);
      ctx.events.push({ type: 'missionChanged', player: pl.id });
    }
  }
}

/** Troca uma missão de eliminar pelo objetivo reserva dela. */
export function fallbackMission(mission: MissionDef, defaultCount: number): MissionDef {
  if (mission.type !== 'eliminate') return mission;
  const check = mission.fallback ?? { type: 'territories' as const, count: defaultCount };
  return { ...check, id: mission.fallback ? `${mission.id}:fallback` : FALLBACK_MISSION_ID, fallbackOf: mission.id };
}
